if (!customElements.get('tbw-jca-product-comparison')) {
  customElements.define(
    'tbw-jca-product-comparison',
    class TBWJCAProductComparison extends HTMLElement {
      constructor() {
        super();

        this.abortController = null;
        this.idleCallbackId = null;
        this.intersectionObserver = null;
        this.hasStarted = false;
      }

      connectedCallback() {
        if (this.hasStarted) return;

        this.hasStarted = true;
        this.setAttribute('aria-busy', 'true');
        this.dataset.state = 'pending';

        this.scheduleLoad();
      }

      disconnectedCallback() {
        if (this.abortController) {
          this.abortController.abort();
        }

        if (
          this.idleCallbackId !== null &&
          'cancelIdleCallback' in window
        ) {
          window.cancelIdleCallback(this.idleCallbackId);
        }

        if (this.intersectionObserver) {
          this.intersectionObserver.disconnect();
        }
      }

      scheduleLoad() {
        if ('requestIdleCallback' in window) {
          this.idleCallbackId = window.requestIdleCallback(
            () => this.init(),
            { timeout: 1500 }
          );

          return;
        }

        if (
          window.FoxTheme &&
          FoxTheme.Motion &&
          typeof FoxTheme.Motion.inView === 'function'
        ) {
          FoxTheme.Motion.inView(
            this,
            () => this.init(),
            { margin: '0px 0px 400px 0px' }
          );

          return;
        }

        if ('IntersectionObserver' in window) {
          this.intersectionObserver = new IntersectionObserver(
            (entries, observer) => {
              const shouldLoad = entries.some(
                (entry) => entry.isIntersecting
              );

              if (!shouldLoad) return;

              observer.disconnect();
              this.intersectionObserver = null;
              this.init();
            },
            {
              rootMargin: '0px 0px 400px 0px'
            }
          );

          this.intersectionObserver.observe(this);
          return;
        }

        window.setTimeout(() => this.init(), 0);
      }

      async init() {
        const requestUrl = this.dataset.url;

        if (!requestUrl) {
          this.handleEmpty('missing_url');
          return;
        }

        this.dataset.state = 'loading';
        this.abortController = new AbortController();

        try {
          const response = await fetch(requestUrl, {
            credentials: 'same-origin',
            headers: {
              'X-Requested-With': 'XMLHttpRequest'
            },
            signal: this.abortController.signal
          });

          if (!response.ok) {
            throw new Error(
              `TBW JCA comparison request failed with status ${response.status}`
            );
          }

          const responseText = await response.text();
          const responseDocument = new DOMParser().parseFromString(
            responseText,
            'text/html'
          );

          const renderedComparison = responseDocument.querySelector(
            'tbw-jca-product-comparison'
          );

          const renderedContainer = renderedComparison
            ? renderedComparison.querySelector(
                '.tbw-jca-product-comparison__container'
              )
            : null;

          if (!renderedComparison || !renderedContainer) {
            this.handleEmpty('no_comparison_products');
            return;
          }

          this.innerHTML = renderedComparison.innerHTML;

          if (renderedComparison.dataset.source) {
            this.dataset.source = renderedComparison.dataset.source;
          }

          if (renderedComparison.dataset.hideEmptyRows) {
            this.dataset.hideEmptyRows =
              renderedComparison.dataset.hideEmptyRows;
          }

          if (renderedComparison.dataset.highlightCurrentProduct) {
            this.dataset.highlightCurrentProduct =
              renderedComparison.dataset.highlightCurrentProduct;
          }

          this.processRows();

          this.removeAttribute('aria-busy');
          this.dataset.state = 'loaded';

          this.dispatchEvent(
            new CustomEvent(
              'tbw-jca-product-comparison:loaded',
              {
                bubbles: true,
                detail: {
                  source: this.dataset.source || 'unknown'
                }
              }
            )
          );
        } catch (error) {
          if (error && error.name === 'AbortError') return;

          console.error(error);
          this.handleError(error);
        }
      }

      processRows() {
        const shouldHideEmptyRows =
          this.dataset.hideEmptyRows === 'true';

        const rows = this.querySelectorAll(
          '.tbw-jca-product-comparison__table tr'
        );

        rows.forEach((row) => {
          const valueCells = Array.from(
            row.querySelectorAll(
              'td[data-comparison-value-present]'
            )
          );

          const hasAnyValue = valueCells.some(
            (cell) =>
              cell.dataset.comparisonValuePresent === 'true'
          );

          row.dataset.hasValue = String(hasAnyValue);

          if (
            shouldHideEmptyRows &&
            valueCells.length > 0 &&
            !hasAnyValue
          ) {
            row.hidden = true;
          }
        });
      }

      handleEmpty(reason) {
        this.removeAttribute('aria-busy');
        this.dataset.state = 'empty';

        this.dispatchEvent(
          new CustomEvent(
            'tbw-jca-product-comparison:empty',
            {
              bubbles: true,
              detail: { reason }
            }
          )
        );

        this.removeSection();
      }

      handleError(error) {
        this.removeAttribute('aria-busy');
        this.dataset.state = 'error';

        this.dispatchEvent(
          new CustomEvent(
            'tbw-jca-product-comparison:error',
            {
              bubbles: true,
              detail: {
                message:
                  error instanceof Error
                    ? error.message
                    : String(error)
              }
            }
          )
        );

        this.removeSection();
      }

      removeSection() {
        const section = this.closest('.shopify-section');

        if (section) {
          section.remove();
          return;
        }

        this.remove();
      }
    }
  );
}
